// A payment while the customer is on the phone (F-40), on the call's customer card: what they owe,
// "Text a pay link" for each open invoice (payments.sendPayLink: they pay on their phone, the office
// never hears a card number), the row turning Paid as it comes in, and Record a payment for money
// already in hand (the books' RecordPayment). `tick` is bumped by the Calls screen's own stream when
// a payment or an invoice changes, so this re-reads without a stream of its own.

import { useEffect, useState } from 'react';
import { timeText } from '@flowology/kernel';
import { client } from '../api.ts';
import { errorText } from '../books/ui.tsx';
import { money } from '../books/model.ts';
import { RecordPayment } from '../books/RecordPayment.tsx';
import type { Money } from '../history/history-model.ts';
import { SaveButton } from '../shared/SaveButton.tsx';
import { phoneText, type Customer } from '../types.ts';
import {
  openInvoices,
  payLinkTargets,
  payState,
  shownInvoices,
  watchAfterRecording,
  type Sent,
  type Watch,
} from './call-payment-model.ts';
import '../books/books.css';
import './call-payment.css';

export function CallPayment({
  customer,
  timeZone,
  tick,
}: {
  customer: Customer;
  timeZone: string;
  tick: number;
}) {
  const [m, setM] = useState<Money | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<Record<string, Sent>>({});
  const [paying, setPaying] = useState<{ id: string; at: string } | null>(null);
  const [recorded, setRecorded] = useState<Record<string, Watch>>({});
  const [said, setSaid] = useState<string | null>(null);
  const [moved, setMoved] = useState(0);
  useEffect(() => {
    client
      .call<Money>('customers.money', { customerId: customer.id })
      .then((r) => {
        setM(r);
        setError(null);
      })
      .catch((e: unknown) => setError(errorText(e, 'Could not read what they owe.')));
  }, [customer.id, tick, moved]);
  if (error) return <p className="error small">{error}</p>;
  if (!m) return null;
  const open = openInvoices(m);
  const targets = payLinkTargets(customer);
  const owing = open.reduce((t, i) => t + i.balanceCents, 0);
  // an invoice a link went for, or a payment was recorded on, stays on the list once it's paid, so the
  // office sees it land and the recording's own "Payment recorded." shows (QA-523)
  const shown = shownInvoices(m, { ...recorded, ...sent });
  if (!shown.length) return null;
  return (
    <div className="call-pay" role="group" aria-label="Payment">
      <h3 className="h4">
        {owing ? (
          <>
            Owing {money(owing)} · {open.length === 1 ? '1 open invoice' : `${open.length} open invoices`}
          </>
        ) : (
          'Paid'
        )}
      </h3>
      {said && (
        <p className="notice ok small" role="status">
          {said}
        </p>
      )}
      <ul className="call-pay-list">
        {shown.map((i) => (
          <InvoiceRow
            key={i.id}
            invoice={i}
            sent={sent[i.id]}
            recorded={recorded[i.id]}
            payments={m.payments}
            targets={targets}
            timeZone={timeZone}
            onSent={(s) => setSent({ ...sent, [i.id]: s })}
            onRecord={() => setPaying({ id: i.id, at: new Date().toISOString() })}
          />
        ))}
      </ul>
      {!targets.length && (
        <p className="muted small">
          {customer.doNotContact
            ? 'They’re do-not-contact, so no pay link can go. Record a payment they’ve already made.'
            : 'No phone or email of theirs has agreed to messages, so no pay link can go.'}
        </p>
      )}
      {paying && (
        // the form's own look is the books'
        <div className="books money-pay">
          <RecordPayment
            key={paying.id}
            customerId={customer.id}
            customerName={customer.displayName}
            firstInvoiceId={paying.id}
            timeZone={timeZone}
            toast={setSaid}
            onDone={(done) => {
              // the earliest watch stays: "Paid" counts from when the office first took a payment
              if (done) setRecorded({ ...watchAfterRecording(open, paying.at), ...recorded });
              setPaying(null);
              if (done) setMoved((n) => n + 1);
            }}
          />
        </div>
      )}
    </div>
  );
}

function InvoiceRow({
  invoice: i,
  sent,
  recorded,
  payments,
  targets,
  timeZone,
  onSent,
  onRecord,
}: {
  invoice: Money['invoices'][number];
  sent: Sent | undefined;
  recorded: Watch | undefined;
  payments: Money['payments'];
  targets: ReturnType<typeof payLinkTargets>;
  timeZone: string;
  onSent: (s: Sent) => void;
  onRecord: () => void;
}) {
  const [to, setTo] = useState(targets[0]?.method.id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{
        channel: 'sms' | 'email';
        to: string;
        sentAt: string;
        balanceCents: number;
      }>('payments.sendPayLink', { invoiceId: i.id, ...(to ? { contactMethodId: to } : {}) });
      onSent({
        to: r.channel === 'sms' ? phoneText(r.to) : r.to,
        at: r.sentAt,
        owingCents: r.balanceCents,
      });
    } catch (e) {
      // the server's words: consent, do-not-contact, nothing owing, a void invoice
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const watch = sent ?? recorded;
  const st = watch ? payState(watch, i, payments) : null;
  return (
    <li className="call-pay-row">
      <span>
        <b>{i.number}</b>
        {st?.state !== 'paid' && <> · {money(i.balanceCents)}</>}
        {i.overdue && st?.state !== 'paid' && (
          <>
            {' '}
            <span className="chip say bad">Overdue</span>
          </>
        )}
      </span>
      {st?.state === 'paid' ? (
        <span className="chip say ok" role="status">
          Paid {money(st.paidCents)}
          {st.at ? ` at ${timeText(st.at, timeZone)}` : ''}
        </span>
      ) : (
        <>
          {st?.state === 'part' ? (
            <span className="small" role="status">
              Paid {money(st.paidCents)} so far; {money(i.balanceCents)} still owing.
            </span>
          ) : (
            sent && (
              <span className="small" role="status">
                {sentLine(sent.to, timeText(sent.at, timeZone))}
              </span>
            )
          )}
          <div className="row">
            {targets.length > 1 && (
              <label className="f call-pay-to">
                Send to
                <select className="field" value={to} onChange={(e) => setTo(e.target.value)}>
                  {targets.map((t) => (
                    <option key={t.method.id} value={t.method.id}>
                      {t.contactName} ·{' '}
                      {t.method.kind === 'phone' ? phoneText(t.method.value) : t.method.value}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {targets.length > 0 && (
              <SaveButton
                className="btn soft"
                busy={busy}
                busyLabel="Sending…"
                error={error}
                onClick={() => void send()}
              >
                {sent
                  ? 'Send it again'
                  : targets.find((t) => t.method.id === to)?.method.kind === 'email'
                    ? 'Email a pay link'
                    : 'Text a pay link'}
              </SaveButton>
            )}
            <button type="button" className="btn quiet" onClick={onRecord}>
              Record a payment
            </button>
          </div>
        </>
      )}
    </li>
  );
}

/** "Pay link sent to (250) 555-0105 at 2:02 p.m. Waiting for the payment…": one period after a.m./p.m. */
const sentLine = (to: string, at: string) =>
  `Pay link sent to ${to} at ${at}${at.endsWith('.') ? '' : '.'} Waiting for the payment…`;
